import { FeatureFlagIds } from "../utils/featureFlagIds";
import {
  getAmountToFinance,
  getIncomeAndDebtBreakdown,
  calculatePayment,
  getBig, calculateLTV,
  calculateMaxAmountToFinanceForTier,
  getAmortizationSchedule,
  getSelectedFinanceQuote,
  debtToIncome,
  paymentToIncome,
} from '../utils/quoteUtils';
import {
  getCustomField,
  getAge
} from '../utils/appUtils';
import { useQuery, queryCache } from 'react-query';
import { ApplicationDispatch, ApplicationState } from '../store/application/types';
import { fetchData, postData, fetchFile } from './fetch';
import { useDispatch, useSelector } from 'react-redux';
import { Action, onAction, Context, BusinessRule, BusinessRuleSet, ActionResult } from '../utils/BusinessRulesEngine';
import { Constants } from '../utils/constants';
import {
  getPaymentToIncomeRatioWithPayment,
  getThresholdSectionForValue,
  getTotalRepossessions,
  saveFile
} from '../utils/utils';
import { DealerServiceState } from '../store/dealerService/reducer';
import { INotificationsState } from "../store/Notifications/reducer";
import {getFinanceQuoteAmountFinanced, getFinanceQuoteValues} from "../utils/LoanCalcUtils";
import {
  add, divide, getMonthsElapsed,
  ApplicationQuoteType,
  getNumberOfPaymentsPerYearForCarletonPaymentFrequency, multiply, pow, roundNumber, subtract
} from "@amsanalytics/react-shared-library";
import { AppState } from '../store/rootReducer';
import { ILoggedInEmployee } from '../Interfaces/Admin/ILoggedInEmployee';
import Big from 'big.js';
import { useConfiguration } from './ConfigurationData';
import {getAppQuoteByType} from "../Components/Quoters/IndirectQuoter/IndirectQuoterUtils";
import {
  getLeaseQuote,
  getLeaseFullPayment,
  getLeaseBasePayment,
  getLeaseTotalOfPayments,
  getLeaseTotalFees,
  getLeaseCashDown,
  getLeaseDeferredDownPayments,
  getLeaseTotalDeferredDown,
  getLeasePaymentToIncome,
  getLeaseDebtToIncome,
  getLeaseMaturityDate,
  getLeaseAmortizationSchedule,
} from '../utils/leaseRuleHelpers';

export const useBusinessRuleSets = (activeUser: ILoggedInEmployee | undefined) => {
  const dispatch = useDispatch<ApplicationDispatch>();

  const getBusinessRuleSets = async (key: string) => {
    if (!activeUser) return [];
    const url = `api/BusinessRules/Sets`;
    const response = await fetchData(url, dispatch) as BusinessRuleSet[];
    return response;
  }

  return useQuery(
    ['BusinessRuleSets'],
    getBusinessRuleSets,
    {
      refetchOnWindowFocus: false,
    }
  );
}

/** Assumes that the businessRuleSets are already ordered by StartDate descending. */
export const getCurrentBusinessRuleSet = (businessRuleSets: BusinessRuleSet[], effectiveDate: Date) => {
  for (const businessRuleSet of businessRuleSets) {
    if (businessRuleSet.startDate && (new Date(businessRuleSet.startDate) <= new Date(effectiveDate))) {
      return businessRuleSet;
    }
  }
  return undefined;
}

export const setBusinessRuleSetId = (businessRuleSetId?: number) => {
  queryCache.setQueryData('BusinessRuleSetId', businessRuleSetId);
}
export const useBusinessRuleSetId = () => {
  return useQuery('BusinessRuleSetId',
    async () => undefined as number | undefined,
    {
      manual: true,
      initialData: undefined,
      cacheTime: undefined,
      staleTime: undefined,
    }
  );
}

export const downloadBusinessRuleSet = async (businessRuleSetId: number, guessedFileName: string, dispatch: ApplicationDispatch) => {
  const url = `api/BusinessRules/Download?businessRuleSetId=${businessRuleSetId}`;
  const response = await fetchFile(url, dispatch);
  if (!response.ok) {
    const json = await response.json();
    const msg = `Unable to download file: ${JSON.stringify(json)}`;
    dispatch({ type: "SET_SERVER_MESSAGE", payload: { message: msg, isError: true } });
    return; /* quit early */
  }
  await saveFile(response, guessedFileName);
}

export const useBusinessRules = () => {
  const dispatch = useDispatch<ApplicationDispatch>();
  const { data: businessRuleSetId } = useBusinessRuleSetId();

  const getBusinessRules = async (key: string, businessRuleSetId?: number) => {
    const url = `api/BusinessRules?businessRuleSetId=${businessRuleSetId === 0 ? 0 : (businessRuleSetId || '')}`;
    const response = await fetchData(url, dispatch) as BusinessRule[];
    return response;
  }

  return useQuery(
    ['BusinessRules', businessRuleSetId],
    getBusinessRules,
    {
      refetchOnWindowFocus: false,
    }
  )
}

export const sortByActionSortOrder = (rules?: BusinessRule[], action?: string) => {
  const result = rules
    ?.map(r => ({
      rule: r,
      action: r.businessRuleActions?.find(x =>
        !x.deleted && x.action === action
      )
    }))
    ?.filter(({ rule, action }) => !rule.deleted && !!action)
    ?.sort((a, b) => (a.action?.sortOrder ?? 0) - (b.action?.sortOrder ?? 0))
    ?.map(({ rule, action }) => rule)
    ?? [];
  return result;
}

export const useBusinessRulesEngine = (): BusinessRulesEngine => {
  const applicationState = useSelector((state: AppState) => state.applications);
  const dealerServiceState = useSelector((state: AppState) => state.dealerService);
  const notificationsState = useSelector((state: AppState) => state.notifications);
  const { data: configuration } = useConfiguration();
  const currentDate = new Date();
  const { data: rules } = useBusinessRules();
  const process = (action: Action, appState?: ApplicationState, dealerService?: DealerServiceState, notifications?: INotificationsState) => {
    const configRules = rules?.filter(r =>
      !r.deleted && r.type === 'Config'
    ) ?? [];
    const rulesForAction = sortByActionSortOrder(rules, action.action);
    const combinedRules = [
      ...configRules,
      ...rulesForAction,
    ];
    const context: Context = {
      applicationState: appState ?? applicationState,
      dealerServiceState: dealerService ?? dealerServiceState,
      // Rules push lease field errors into an empty list each run, so a fixed field clears.
      notificationsState: { ...(notifications ?? notificationsState), leaseQuoteErrors: [] },
      currentDate,
      rules: combinedRules,
      debugMode: true,
      excludePaymentFromDebtToIncome: configuration?.featureFlags?.[FeatureFlagIds.ExcludePaymentFromDebtToIncome] === true,
      attachDebugger: false,
      config: {},
      ruleId: undefined,
      utilities: {
        getAge,
        multiply,
        divide,
        pow,
        add,
        subtract,
        getAmountToFinance,
        getIncomeAndDebtBreakdown: getIncomeAndDebtBreakdown,
        calculatePayment,
        calculateLTV,
        getBig,
        getCustomField,
        roundNumber,
        calculateMaxAmountToFinanceForTier,
        getAmortizationSchedule,
        getFinanceQuoteValues,
        constants: Constants,
        getSelectedFinanceQuote,
        getMonthsElapsed: getMonthsElapsed,
        debtToIncome: debtToIncome,
        paymentToIncome: paymentToIncome,
        getNumberOfPaymentsPerYearForCarletonPaymentFrequency: getNumberOfPaymentsPerYearForCarletonPaymentFrequency,
        applicationQuoteTypes: ApplicationQuoteType,
        big: Big,
        getThresholdSectionForValue,
        getAppQuoteByType,
        getFinanceQuoteAmountFinanced,
        getTotalRepossessions,
        getPaymentToIncomeRatioWithPayment,
        getLeaseQuote,
        getLeaseFullPayment,
        getLeaseBasePayment,
        getLeaseTotalOfPayments,
        getLeaseTotalFees,
        getLeaseCashDown,
        getLeaseDeferredDownPayments,
        getLeaseTotalDeferredDown,
        getLeasePaymentToIncome,
        getLeaseDebtToIncome,
        getLeaseMaturityDate,
        getLeaseAmortizationSchedule
      }
    };
    const actionResult = onAction(action, context);
    return actionResult;
  }
  return process;
}

export const updateBusinessRuleSet = async (businessRuleSet: BusinessRuleSet) => {
  const url = `api/BusinessRules/Sets/Update`;
  const response = await postData(url, businessRuleSet);
  await queryCache.refetchQueries("BusinessRuleSets");
}

export const copyBusinessRuleSet = async (businessRuleSetId?: number) => {
  const url = `api/BusinessRules/Sets/Copy`;
  const response = await postData(url, businessRuleSetId);
  await queryCache.refetchQueries("BusinessRuleSets");
}

export const updateBusinessRule = async (businessRule: BusinessRule) => {
  const url = `api/BusinessRules/update`;
  const response = await postData(url, businessRule);
  await queryCache.refetchQueries("BusinessRules");
}

export const assignBusinessRules = async (action: string, ids: number[], currentBusinessRuleSetId: number) => {
  const url = `api/BusinessRules/assign/${action}/${currentBusinessRuleSetId}`;
  const response = await postData(url, ids);
  await queryCache.refetchQueries("BusinessRules");
}

export const BusinessActions = {
  // Application
  "New": { type: "Application" },
  "Load App": { type: "Application" },
  "Save": { type: "Application" },
  "Cancel": { type: "Application" },
  "Submit": { type: "Application" },
  "Conditionally Approve": { type: "Application" },
  "Satisfy Conditional Approval": { type: "Application" },
  "Approve": { type: "Application" },
  "Deny": { type: "Application" },
  "Deal Lost": { type: "Application" },
  "Desk": { type: "Application" },
  "Modify": { type: "Application" },
  "Download App PDF": { type: "Application" },
  "Notes": { type: "Application" },
  "Unlock App": { type: "Application" },
  "Rescore": { type: "Application" },
  "Print Quote": { type: "Application" },
  "Print Docs": { type: "Application" },
  "Merge": { type: "Application" },
  "Make Primary Applicant": { type: "Application" },
  "Remove Vehicle": { type: "Application" },
  "Select Vehicle": { type: "Application" },
  "Unmerge": { type: "Application" },
  "Copy Application": { type: "Application" },

  // Tabs
  "View App": { type: "Tabs" },
  "View Overview": { type: "Tabs" },
  "View Applicant": { type: "Tabs" },
  "View Co-Applicant 1": { type: "Tabs" },
  "View Co-Applicant 2": { type: "Tabs" },
  "View Information": { type: "Tabs" },
  "View Employment": { type: "Tabs" },
  "View Income and Debt": { type: "Tabs" },
  "View References": { type: "References" },
  "View Bureau": { type: "Tabs" },
  "View Vehicle": { type: "Tabs" },
  "View Quoter": { type: "Quoter" },
  "View Custom Fields": { type: "Tabs" },
  "View Quote": { type: "Tabs" },
  "View Lease": { type: "Tabs" },
  "View Documents": { type: "Tabs" },
  "View Notes": { type: "Tabs" },
  "View Underwriter Notes": { type: "Tabs" },
  "View Funding": { type: "Tabs" },
  "View Audit Log": { type: "Tabs" },
  "View JD Powersport History": { type: "Tabs" },
  "View Notifications": { type: "Tabs" },
  "View Evaluation": { type: "Tabs" },
  "View Income Verification": { type: "Tabs" },
  "View Validation Rules": { type: "Tabs" },

  // Documents
  "Modify Document": { type: "Documents" },
  "Upload Documents": { type: "Documents" },
  "Download Document": { type: "Documents" },
  "Delete Document": { type: "Documents" },
  "Request Documents": { type: "Documents" },

  // Stipulations
  "View Stipulation(s)": { type: "Stipulations" },
  "Add Remove Stipulations": { type: "Stipulations" },
  "Update Stipulation": { type: "Stipulations" },

  // Budget
  "View Budget": { type: "Budget" },
  "Edit Budget": { type: "Budget" },
  "Save Budget": { type: "Budget" },
  "Reset Budget": { type: "Budget" },

  // Account
  "Login": { type: "Account" },
  "Logout": { type: "Account" },
}
export type BusinessAction = keyof typeof BusinessActions;
export type BusinessRulesEngine = (action: Action, appState?: ApplicationState, dealerServiceState?: DealerServiceState) => ActionResult;